import React from 'react';
import { subDays, format } from 'date-fns';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { PieChart, BarChart, Download, DollarSign } from 'lucide-react';
import { BucketCategory } from '../types';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Define category colors and labels
const CATEGORY_COLORS: Record<BucketCategory, string> = {
  groceries: '#10B981', // green-500
  dining: '#F59E0B', // yellow-500
  entertainment: '#8B5CF6', // purple-500
  shopping: '#EC4899', // pink-500
  transportation: '#3B82F6', // blue-500
  utilities: '#6B7280', // gray-500
  healthcare: '#EF4444', // red-500
  personal: '#8B5CF6', // violet-500
  gifts: '#F43F5E', // rose-500
  pet: '#F59E0B', // amber-500
  other: '#9CA3AF' // gray-400
};

const CATEGORY_LABELS: Record<BucketCategory, string> = {
  groceries: 'Groceries',
  dining: 'Dining Out',
  entertainment: 'Entertainment',
  shopping: 'Shopping',
  transportation: 'Transportation',
  utilities: 'Utilities',
  healthcare: 'Healthcare',
  personal: 'Personal Care',
  gifts: 'Gifts',
  pet: 'Pet Expenses',
  other: 'Other'
};

interface CategorySpending {
  category: BucketCategory;
  total: number;
  transactions: Array<{
    description: string;
    amount: number;
    date: Date;
  }>;
}

export default function BucketSpendingReport() {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [categorySpending, setCategorySpending] = React.useState<CategorySpending[]>([]);
  const [totalSpent, setTotalSpent] = React.useState(0);

  React.useEffect(() => {
    const fetchSpending = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const thirtyDaysAgo = subDays(new Date(), 30);
        const bucketsRef = collection(db, 'budgetBuckets');
        const q = query(bucketsRef, where('userId', '==', user.uid));
        const snapshot = await getDocs(q);

        const spendingByCategory = new Map<BucketCategory, CategorySpending>();

        // Initialize spending for all categories
        Object.keys(CATEGORY_LABELS).forEach((category) => {
          spendingByCategory.set(category as BucketCategory, {
            category: category as BucketCategory,
            total: 0,
            transactions: []
          });
        });

        let total = 0;

        // Process all buckets and their transactions
        snapshot.docs.forEach(doc => {
          const bucket = doc.data();
          const category = bucket.category as BucketCategory;
          
          if (bucket.transactions) {
            bucket.transactions.forEach((transaction: any) => {
              const transactionDate = transaction.date instanceof Timestamp 
                ? transaction.date.toDate() 
                : new Date(transaction.date);

              // Only include transactions from last 30 days and negative amounts (spending)
              if (transactionDate >= thirtyDaysAgo && transaction.amount < 0) {
                const amount = Math.abs(transaction.amount);
                const categoryData = spendingByCategory.get(category)!;
                
                categoryData.total += amount;
                categoryData.transactions.push({
                  description: transaction.description,
                  amount: amount,
                  date: transactionDate
                });

                total += amount;
              }
            });
          }
        });

        // Convert map to array and sort by total spent
        const spendingArray = Array.from(spendingByCategory.values())
          .filter(category => category.total > 0)
          .sort((a, b) => b.total - a.total);

        setCategorySpending(spendingArray);
        setTotalSpent(total);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching spending data:', error);
        setError('Failed to load spending data');
        setLoading(false);
      }
    };

    fetchSpending();
  }, [user]);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;

    // Title
    doc.setFontSize(24);
    doc.setTextColor(31, 41, 55); // text-gray-900
    doc.text('30 Day Spending Report', margin, margin);

    // Date Range
    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128); // text-gray-500
    doc.text(
      `${format(subDays(new Date(), 30), 'MMM d, yyyy')} - ${format(new Date(), 'MMM d, yyyy')}`,
      margin,
      margin + 10
    );

    // Total Spending
    doc.setFontSize(16);
    doc.setTextColor(79, 70, 229); // text-indigo-600
    doc.text(`Total Spent: $${totalSpent.toFixed(2)}`, margin, margin + 20);

    let yPos = margin + 35;

    // Category Summary
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text('Spending by Category', margin, yPos);
    yPos += 10;

    // Category Breakdown
    categorySpending.forEach((category) => {
      const percentage = (category.total / totalSpent) * 100;
      
      // Category header
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.text(CATEGORY_LABELS[category.category], margin, yPos);
      doc.text(`$${category.total.toFixed(2)} (${percentage.toFixed(1)}%)`, pageWidth - margin - 40, yPos, { align: 'right' });
      
      // Progress bar
      const barWidth = 120;
      const barHeight = 3;
      doc.setFillColor(229, 231, 235); // bg-gray-200
      doc.rect(margin, yPos + 3, barWidth, barHeight, 'F');
      doc.setFillColor(CATEGORY_COLORS[category.category]);
      doc.rect(margin, yPos + 3, (barWidth * percentage) / 100, barHeight, 'F');
      
      yPos += 15;

      // Check if we need a new page for transactions
      if (yPos > doc.internal.pageSize.height - 30) {
        doc.addPage();
        yPos = margin;
      }

      // Transactions table
      if (category.transactions.length > 0) {
        doc.autoTable({
          startY: yPos,
          head: [['Date', 'Description', 'Amount']],
          body: category.transactions.map(t => [
            format(t.date, 'MMM d'),
            t.description,
            `$${t.amount.toFixed(2)}`
          ]),
          margin: { left: margin },
          headStyles: { fillColor: CATEGORY_COLORS[category.category] },
          styles: { fontSize: 10 },
          columnStyles: {
            0: { cellWidth: 30 },
            2: { cellWidth: 30, halign: 'right' }
          }
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
      }

      // Check if we need a new page for the next category
      if (yPos > doc.internal.pageSize.height - 40) {
        doc.addPage();
        yPos = margin;
      }
    });

    // Save the PDF
    doc.save(`spending_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 py-8">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart className="w-6 h-6 mr-2" />
            30 Day Spending Report
          </h2>
          <button
            onClick={handleExportPDF}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </button>
        </div>

        {/* Total Spending Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Spent (Last 30 Days)</p>
              <p className="text-3xl font-bold text-indigo-600">
                ${totalSpent.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-full">
              <DollarSign className="w-8 h-8 text-indigo-600" />
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Spending by Category</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {categorySpending.map((category) => {
                const percentage = (category.total / totalSpent) * 100;
                
                return (
                  <div key={category.category}>
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full bg-[${CATEGORY_COLORS[category.category]}] mr-2`} />
                        <span className="text-sm font-medium text-gray-900">
                          {CATEGORY_LABELS[category.category]}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-gray-900">
                          ${category.total.toFixed(2)}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: CATEGORY_COLORS[category.category]
                        }}
                      />
                    </div>

                    {/* Transaction List */}
                    <div className="mt-2 pl-5 space-y-1">
                      {category.transactions.map((transaction, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <div className="text-gray-600">
                            {transaction.description}
                            <span className="text-gray-400 ml-2">
                              {format(transaction.date, 'MMM d')}
                            </span>
                          </div>
                          <div className="text-gray-900 font-medium">
                            ${transaction.amount.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {categorySpending.length === 0 && (
                <div className="text-center py-12">
                  <PieChart className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No spending data</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No transactions found in the last 30 days.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}